import { forwardRef, type ReactNode } from "react";
import { cn } from "~/lib/cn";

interface InputProps extends React.ComponentProps<"input"> {
  label?: string;
  /** Renders next to the label (e.g. info button). */
  labelAside?: ReactNode;
  error?: string;
  /** @deprecated No-op kept for API compatibility. */
  minimal?: boolean;
  /** Extra class applied to the .af-label element. */
  labelClassName?: string;
  /** Slot rendered inside .af-input-wrap, before the input (e.g. a lock/phone icon). */
  leading?: ReactNode;
  /** Slot rendered inside .af-input-wrap, after the input (e.g. a password toggle). */
  trailing?: ReactNode;
  /** Slot rendered just below .af-input-wrap (e.g. a strength meter). */
  below?: ReactNode;
  /**
   * Translated word for the optional tag (e.g. "opcional"), shown next to the label.
   * Must be a meaningful word (not punctuation): the chip lives inside `<label>`, so
   * it becomes part of the input's accessible name announced by screen readers.
   */
  optional?: string;
  /**
   * `public` (T872 S3) — the October delivery's `.test-field`: a sentence-case 14px sans
   * label over a 48px white field, the error line under it. No wrap, so `leading`,
   * `trailing`, `below` and `labelAside` are not rendered in this variant. `default` is
   * the auth-form field (`.af-*`).
   */
  variant?: "default" | "public";
}

export const Input = forwardRef<HTMLInputElement, InputProps>(
  function Input(
    { label, labelAside, error, minimal: _minimal, labelClassName, leading, trailing, below, optional, className, id, readOnly, disabled, variant = "default", ...props },
    ref,
  ) {
    const inputId = id ?? label?.toLowerCase().replace(/\s+/g, "-");
    const helpId = error ? `${inputId}-error` : undefined;
    const optionalChip = optional ? <span className="opt">{' '}{optional}</span> : null;

    if (variant === "public") {
      return (
        <div className={cn("pub-field", error && "is-error")}>
          {label && (
            <label htmlFor={inputId} className={cn("pub-field-label", labelClassName)}>
              {label}
              {optional ? <span className="pub-field-optional"> ({optional})</span> : null}
            </label>
          )}
          <input
            ref={ref}
            id={inputId}
            className={cn("pub-field-input", className)}
            aria-invalid={error ? true : undefined}
            aria-describedby={helpId}
            readOnly={readOnly}
            disabled={disabled}
            {...props}
          />
          {error && (
            <p id={helpId} className="pub-field-error" role="alert">
              {error}
            </p>
          )}
        </div>
      );
    }

    return (
      <div className="af-field">
        {label && !labelAside && (
          <label htmlFor={inputId} className={cn("af-label", labelClassName)}>
            <span>{label}</span>
            {optionalChip}
          </label>
        )}
        {label && labelAside && (
          <div className="flex items-start justify-between gap-2">
            <label htmlFor={inputId} className={cn("af-label", labelClassName)}>
              <span>{label}</span>
              {optionalChip}
            </label>
            {labelAside}
          </div>
        )}
        <div
          className={cn(
            "af-input-wrap",
            error && "is-error",
            readOnly && "is-readonly",
            disabled && "is-disabled",
          )}
        >
          {leading ? <span className="af-input-leading">{leading}</span> : null}
          <input
            ref={ref}
            id={inputId}
            className={cn("af-input", className)}
            aria-invalid={error ? true : undefined}
            aria-describedby={helpId}
            readOnly={readOnly}
            disabled={disabled}
            {...props}
          />
          {trailing}
        </div>
        {below}
        {error && (
          <p id={helpId} className="af-helper is-error" role="alert">
            {error}
          </p>
        )}
      </div>
    );
  },
);
